package com.eladfinish.bible.contest.network

import com.eladfinish.bible.contest.extensions.convertToDataClass
import io.github.aakira.napier.Napier
import io.ktor.client.call.body
import io.ktor.client.statement.HttpResponse
import io.ktor.utils.io.charsets.Charset
import io.ktor.utils.io.charsets.decode

suspend fun HttpResponse.bodyAsTextUTF16(): String {
    val originCharset = Charset.forName("UTF-16BE")
    val decoder = originCharset.newDecoder()
    return decoder.decode(input = body()).removePrefix("﻿")
}

suspend inline fun <reified T> HttpResponse.bodyUTF8(): T {
    return decodeUtf8(body()).let {
        Napier.d("bodyUTF8\n$it")
        it.convertToDataClass()
    }
}

/**
 * UTF-8 bytes as text, without a leading byte-order mark. Pure Kotlin (no ktor Charset), so it
 * behaves the same on every target — use it, not [bodyUTF16], for UTF-8/ASCII sources such as
 * the dates-in-bible JSON, which [decodeUtf16BigEndian] would turn into gibberish.
 */
fun decodeUtf8(bytes: ByteArray): String =
    bytes.decodeToString().removePrefix("\uFEFF")

suspend inline fun <reified T> HttpResponse.bodyUTF16(): T {
    // Decoded by hand (not via Charset.forName("UTF-16BE")) because ktor-io's Kotlin/JS Charset
    // implementation only supports UTF-8 and ISO-8859-1 and throws for anything else.
    return decodeUtf16BigEndian(body()).removePrefix("﻿").let {
        Napier.d("bodyUTF16\n$it")
        it.convertToDataClass()
    }
}

fun decodeUtf16BigEndian(bytes: ByteArray): String {
    val builder = StringBuilder(bytes.size / 2)
    var i = 0
    while (i + 1 < bytes.size) {
        val high = bytes[i].toInt() and 0xFF
        val low = bytes[i + 1].toInt() and 0xFF
        builder.append(((high shl 8) or low).toChar())
        i += 2
    }
    return builder.toString()
}

/**
 * Decodes the body as UTF-16 using the byte-order mark to pick endianness (falls back to
 * big-endian if no BOM is present), unlike [bodyUTF16] which always assumes big-endian. Use this
 * for sources known to be UTF-16 *little*-endian with a LE BOM (e.g. the syllabus .txt files,
 * which mirror the app's legacy UTF-16LE local assets).
 */
suspend fun HttpResponse.bodyAsTextUtf16Bom(): String {
    val originCharset = Charset.forName("UTF-16")
    val decoder = originCharset.newDecoder()
    return decoder.decode(input = body())
}
